import { useState, useEffect, useRef } from "react";
import { BookAppointmentModal } from "components/BookAppointment";
import DoctorCarousel from "components/DoctorCarousel";
import HeroReadMore from "components/HeroReadMore";
import scrollToSection from "utils/scrollToSection";
import mapsUrl from "utils/mapsUrl";
import visibleStats from "utils/visibleStats";
import AddressesModal from "components/site/AddressesModal";
import buildImgTransform from "utils/imgTransform";
import PhoneIcon from "components/site/PhoneIcon";
import MapPinIcon from "components/site/MapPinIcon";
import { SpotlightCard, useParallax, Masonry, ChromaVeil, chromaTrack } from "components/site/fx";
import GalleryCarousel from "components/site/GalleryCarousel";
import { GradientText, StarBorder } from "components/site/textfx";
import SplashCursor from "components/site/SplashCursor";

const CLEAN_HERO = "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=1600&q=80";
const CLEAN_GALLERY = [
  "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=900&q=80",
  "https://images.unsplash.com/photo-1551190822-a9333d879b1f?w=900&q=80",
  "https://images.unsplash.com/photo-1582719508461-905c673771fd?w=900&q=80",
  "https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?w=900&q=80",
];

// ── WhatsApp ─────────────────────────────────────────────────────────────────
const buildWhatsAppHref = (cfg) => {
  if (!cfg?.whatsapp_enabled || !cfg?.contact_phone) return null;
  const digits = String(cfg.contact_phone).replace(/\D/g, '');
  if (!digits) return null;
  const base = `https://wa.me/${digits}`;
  return cfg.whatsapp_message
    ? `${base}?text=${encodeURIComponent(cfg.whatsapp_message)}`
    : base;
};
const WhatsAppIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// ── Counter ───────────────────────────────────────────────────────────────────
function Counter({ target }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      obs.disconnect();
      const end = parseInt(target, 10) || 0; if (!end) return;
      const step = Math.ceil(end / 55);
      let v = 0;
      const t = setInterval(() => { v = Math.min(v + step, end); setVal(v); if (v >= end) clearInterval(t); }, 22);
    }, { threshold: 0.5 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [target]);
  return <span ref={ref}>{val.toLocaleString()}</span>;
}

// ── Image Carousel ────────────────────────────────────────────────────────────
function CleanCarousel({ images, accent }) {
  if (!images.length) return null;
  return (
    <section style={{ padding: '72px 24px', background: '#f4f4f0' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <p style={{ color: '#888', fontSize: '0.75rem', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 8 }}>Gallery</p>
          <h2 style={{ fontSize: 'clamp(20px, 2.5vw, 32px)', fontWeight: 700, fontFamily: "'Kimbiso', 'DM Sans', sans-serif", color: '#1a1a1a' }}>Our Clinic</h2>
        </div>
        <GalleryCarousel images={images} accent={accent} />
      </div>
    </section>
  );
}

// ── Condition enrichment ──────────────────────────────────────────────────────
const COND_LIBRARY = [
  { kw: ['skin','eczema','psoriasis','acne','allerg','rash','dermat'], badge: 'Dermatology', period: '4–12 wks', ages: 'All ages', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
  { kw: ['thyroid','diabetes','hormone','endocrin'], badge: 'Endocrine', period: 'Ongoing', ages: 'Adults 20+', icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z' },
  { kw: ['asthma','sinusit','respirat','lung','breath'], badge: 'Respiratory', period: '6–8 wks', ages: 'All ages', icon: 'M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z' },
  { kw: ['digest','gastric','stomach','ibs','bowel','liver'], badge: 'Gastro', period: '2–6 wks', ages: 'All ages', icon: 'M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z' },
  { kw: ['hair','scalp','alopecia'], badge: 'Trichology', period: '8–16 wks', ages: 'Ages 15+', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
  { kw: ['women','female','gynec','menstr','pcod','pcos'], badge: "Women's Health", period: 'Ongoing', ages: 'Women', icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z' },
  { kw: ['child','pediatr','infant','baby'], badge: 'Paediatrics', period: 'As needed', ages: 'Under 14', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' },
  { kw: ['stress','anxiety','sleep','mental','depress','insomnia'], badge: 'Mind & Body', period: '4–10 wks', ages: 'Adults', icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z' },
  { kw: ['joint','arthrit','rheumat','bone','back','pain','spine'], badge: 'Musculoskeletal', period: '6–12 wks', ages: 'Adults 30+', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' },
];
const COND_EMOJIS = ['🌿','🌱','💊','🧘','🫁','🌸','👶','🧠','🦴','💧','❤️','🩺'];
function enrichCondition(str) {
  const s = str.toLowerCase();
  for (const r of COND_LIBRARY) { if (r.kw.some(k => s.includes(k))) return r; }
  return { badge: 'General Care', period: 'Varies', ages: 'All ages', icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' };
}

// ── Scroll reveal ─────────────────────────────────────────────────────────────
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(entries => entries.forEach(e => {
      if (e.isIntersecting) { e.target.style.opacity = '1'; e.target.style.transform = 'translateY(0) scale(1)'; e.target.style.filter = 'blur(0)'; }
    }), { threshold: 0.1 });
    ref.current.querySelectorAll('[data-reveal]').forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);
  return ref;
}
const R = { opacity: 0, transform: 'translateY(32px) scale(0.98)', filter: 'blur(5px)', transition: 'opacity 0.7s ease, transform 0.7s cubic-bezier(.2,.7,.2,1), filter 0.7s ease' };

// ═════════════════════════════════════════════════════════════════════════════
export default function TemplateClean({ config, companyData }) {
  const [heroRef, heroParallax] = useParallax(0.18);
  const [modalOpen, setModalOpen] = useState(false);
  const [addrOpen, setAddrOpen] = useState(false);
  const [preselect, setPreselect] = useState({ doctorId: null, specId: null });
  const openBookWith = (doctorId, specId) => {
    setPreselect({ doctorId, specId });
    setModalOpen(true);
  };
  const [navScrolled, setNavScrolled] = useState(false);
  const [hoveredWhy, setHoveredWhy] = useState(null);
  const [hoveredFeat, setHoveredFeat] = useState(null);
  const [hoveredCond, setHoveredCond] = useState(null);
  const [hoveredApproach, setHoveredApproach] = useState(null);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [bioDoc, setBioDoc] = useState(null); // doctor card showing full bio (hover/tap)
  const pageRef = useReveal();

  const acc = config.accentColor || '#2563eb';
  const whatsapp = buildWhatsAppHref(config);
  const heroBg = config.heroBannerImage || CLEAN_HERO;
  const gallery = (config.galleryImages || []).filter(Boolean);
  const carousel = (config.galleryCarousel || []).filter(Boolean);
  const imgAdjust = config.imageAdjust || {};
  const stats = config.stats || {};
  const statLabels = config.statLabels || {};
  const ribbonStats = [
    ...visibleStats(config, { bookings: 'Appointments', doctors: 'Expert Doctors', users: 'Happy Patients' }).map((s) => ({ ...s, suffix: '+' })),
    { key: 'days', val: null, label: config.consultationHours || 'Mon – Sat', text: '6 days' },
  ];
  const features = config.features || [];
  const conditions = config.conditions || [];
  const whyChooseUs = config.whyChooseUs || [];
  const testimonials = config.testimonials || [];
  const doctors = (config.doctors || []).filter((d) => d.is_active && d.show_on_website !== false);
  const doctorsSection = config.doctorsSection || { enabled: true, title: 'Meet Our Doctors' };
  const locations = config.locations || [];
  const approach = Array.isArray(config.aboutApproach) ? config.aboutApproach : [];

  const scrollToId = (id) => scrollToSection(id);

  useEffect(() => {
    const fn = () => setNavScrolled(window.scrollY > 20);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  // Palette
  const C = { bg: '#fafaf8', warm: '#f5f4f0', text: '#1a1a1a', mid: '#4a4a4a', muted: '#888', border: 'rgba(0,0,0,0.08)', card: '#fff' };

  return (
    <div ref={pageRef} style={{ fontFamily: "'DM Sans','Segoe UI',system-ui,sans-serif", background: C.bg, color: C.text, minHeight: '100vh', overflowX: 'hidden' }}>
      <style>{`
        *{box-sizing:border-box;margin:0;padding:0}
        h1,h2,h3,p,span{overflow-wrap:break-word}
        /* Minimal theme identity: Kimbiso display headings — natural weight,
           no extra letter-spacing */
        h1,h2{font-family:'Kimbiso','DM Sans',sans-serif!important;font-weight:400!important;letter-spacing:0!important}
        html{scroll-behavior:smooth}
        [id^="cl-"]{scroll-margin-top:84px}
        @media(max-width:768px){
          .cl-nav-links{display:none!important}
          .cl-mob-btn{display:flex!important}
          .cl-hero-ctas{flex-direction:column!important}
          .cl-hero{height:auto!important}
          .cl-hero-inner{height:auto!important;align-items:flex-start!important;padding-top:36px!important;padding-bottom:44px!important}
          .cl-about-split{flex-direction:column!important}
          .cl-contact-grid{grid-template-columns:1fr!important}
          .cl-footer-grid{grid-template-columns:1fr 1fr!important}
          .cl-hide-mob{display:none!important}
        }
        @media(min-width:769px){.cl-mob-btn{display:none!important}.cl-mob-menu{display:none!important}}
      `}</style>

      {/* ── NAV ── */}
      <nav style={{ position: 'sticky', top: 0, zIndex: 100, background: navScrolled ? 'rgba(250,250,248,0.97)' : C.bg, backdropFilter: 'blur(12px)', borderBottom: navScrolled ? `1px solid ${C.border}` : '1px solid transparent', boxShadow: navScrolled ? '0 2px 20px rgba(0,0,0,0.07)' : 'none', transition: 'box-shadow 0.3s', padding: '0 24px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', height: 66, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: acc, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {config.logo ? <img src={config.logo} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', transform: config.logoTransform }} /> : <span style={{ color: '#fff', fontWeight: 800, fontSize: '1rem' }}>{config.companyName?.charAt(0)}</span>}
            </div>
            <span style={{ fontWeight: 700, fontSize: '0.97rem', color: C.text, letterSpacing: '-0.01em' }}>{config.companyName}</span>
          </div>
          <div className="cl-nav-links" style={{ display: 'flex', gap: 4 }}>
            {['About','Services','Reviews','Contact'].filter(l => !((l === 'Reviews' && testimonials.length === 0) || (l === 'Services' && features.length === 0))).map(l => (
              <a key={l} href={`#cl-${l.toLowerCase()}`} onClick={e => { e.preventDefault(); scrollToId(`cl-${l.toLowerCase()}`); }} style={{ padding: '7px 15px', borderRadius: 999, fontSize: '0.88rem', fontWeight: 500, color: C.mid, textDecoration: 'none', transition: 'color 0.25s, background 0.25s, transform 0.25s' }}
                onMouseEnter={e => { e.target.style.color = '#fff'; e.target.style.background = acc; e.target.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={e => { e.target.style.color = C.mid; e.target.style.background = 'transparent'; e.target.style.transform = 'none'; }}>{l}</a>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {whatsapp && (
              <a href={whatsapp} target="_blank" rel="noopener noreferrer" className="cl-hide-mob"
                style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#25d366', color: '#fff', borderRadius: 100, padding: '7px 14px', fontSize: '0.82rem', fontWeight: 600, textDecoration: 'none' }}>
                <WhatsAppIcon size={14} /> WhatsApp
              </a>
            )}
            <button onClick={() => setModalOpen(true)} style={{ background: acc, color: '#fff', border: 'none', borderRadius: 100, padding: '8px 18px', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer' }}>
              Book Appointment
            </button>
            <button className="cl-mob-btn" onClick={() => setMobileMenu(!mobileMenu)} style={{ display: 'none', background: 'none', border: `1px solid ${C.border}`, borderRadius: 8, padding: '7px 9px', cursor: 'pointer', flexDirection: 'column', gap: 4 }}>
              <span style={{ display: 'block', width: 18, height: 2, background: C.text }} />
              <span style={{ display: 'block', width: 18, height: 2, background: C.text }} />
              <span style={{ display: 'block', width: 18, height: 2, background: C.text }} />
            </button>
          </div>
        </div>
        {mobileMenu && (
          <div className="cl-mob-menu" style={{ padding: '12px 24px 20px', borderTop: `1px solid ${C.border}`, background: C.bg, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {['About','Services','Reviews','Contact'].filter(l => !((l === 'Reviews' && testimonials.length === 0) || (l === 'Services' && features.length === 0))).map(l => (
              <a key={l} href={`#cl-${l.toLowerCase()}`} onClick={e => { e.preventDefault(); setMobileMenu(false); scrollToId(`cl-${l.toLowerCase()}`); }} style={{ color: C.text, textDecoration: 'none', padding: '10px 0', borderBottom: `1px solid ${C.border}`, fontSize: '0.95rem' }}>{l}</a>
            ))}
            <button onClick={() => { setModalOpen(true); setMobileMenu(false); }} style={{ marginTop: 12, background: acc, color: '#fff', border: 'none', borderRadius: 50, padding: '12px', fontWeight: 700, cursor: 'pointer' }}>
              Book Appointment
            </button>
          </div>
        )}
      </nav>

      {/* ── HERO ── */}
      <section className="cl-hero" style={{ position: 'relative', height: 'clamp(380px, 55vw, 620px)', overflow: 'hidden' }}>
        <img ref={heroRef} src={heroBg} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', transform: `${heroParallax} scale(1.15)`, transition: 'transform 0.1s linear' }} onError={e => { e.target.src = CLEAN_HERO; }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.35) 70%, rgba(0,0,0,0.15) 100%)' }} />
        <div className="cl-hero-inner" style={{ position: 'relative', zIndex: 1, maxWidth: 1200, margin: '0 auto', padding: 'clamp(48px,8vw,96px) 24px', height: '100%', display: 'flex', alignItems: 'center' }}>
          <div style={{ maxWidth: 620 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 50, padding: '5px 14px', marginBottom: 20 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#86efac', display: 'inline-block' }} />
              <span style={{ color: '#fff', fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Trusted Healthcare</span>
            </div>
            <h1 style={{ color: '#fff', fontSize: 'clamp(30px, 5vw, 60px)', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1, marginBottom: 14, fontFamily: "'Kimbiso', 'DM Sans', sans-serif" }}>
              <GradientText text={config.companyName || ''} colors={['#ffffff', acc, '#ffffff']} />
            </h1>
            {config.tagline && <p style={{ color: 'rgba(255,255,255,0.88)', fontSize: 'clamp(15px, 1.6vw, 20px)', lineHeight: 1.55, marginBottom: 8, fontWeight: 400 }}>{config.tagline}</p>}
            {config.heroSubtext && <p style={{ color: 'rgba(255,255,255,0.72)', fontSize: '0.97rem', lineHeight: 1.7, marginBottom: 28 }}>{config.heroSubtext}</p>}
            <HeroReadMore paragraphs={config.heroExtraParagraphs} heading={config.companyName} linkColor="#fff" />

            <div className="cl-hero-ctas" style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <StarBorder color="#ffffff">
                <button onClick={() => setModalOpen(true)} style={{ background: 'transparent', color: '#fff', border: 'none', borderRadius: 100, padding: '13px 28px', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer' }}>
                  Book Appointment
                </button>
              </StarBorder>
              {whatsapp && (
                <a href={whatsapp} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#25d366', color: '#fff', borderRadius: 100, padding: '13px 24px', fontWeight: 700, fontSize: '0.95rem', textDecoration: 'none' }}>
                  <WhatsAppIcon size={17} /> WhatsApp
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS RIBBON ── */}
      <div style={{ background: C.card, borderBottom: `1px solid ${C.border}` }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
          {ribbonStats.map((s, i) => (
            <div key={s.key || i} style={{ flex: '1 1 160px', textAlign: 'center', padding: '28px 20px', borderRight: i < ribbonStats.length - 1 ? `1px solid ${C.border}` : 'none' }}>
              <div style={{ fontSize: 'clamp(24px, 3vw, 38px)', fontWeight: 800, color: acc, lineHeight: 1, letterSpacing: '-0.02em' }}>
                {s.val != null ? <><Counter target={s.val} />{s.suffix}</> : s.text}
              </div>
              <div style={{ fontSize: '0.76rem', color: C.muted, marginTop: 5, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── ABOUT ── */}
      <section id="cl-about" style={{ padding: 'clamp(56px,8vw,96px) 24px', background: C.bg }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div className="cl-about-split" style={{ display: 'flex', gap: 56, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Left — image mosaic */}
            <div data-reveal style={{ ...R, flex: '0 0 44%', minWidth: 280 }}>
              <Masonry images={(gallery.length > 0 ? gallery : CLEAN_GALLERY).slice(0, 5)} columns={2} gap={10} radius={14} accent={acc} />
            </div>
            {/* Right — text */}
            <div data-reveal style={{ ...R, flex: '1 1 300px', transitionDelay: '0.1s' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <div style={{ width: 32, height: 2, background: acc, borderRadius: 2 }} />
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: acc, textTransform: 'uppercase', letterSpacing: '0.12em' }}>About Us</span>
              </div>
              <h2 style={{ fontSize: 'clamp(24px, 3vw, 38px)', fontWeight: 700, fontFamily: "'Kimbiso', 'DM Sans', sans-serif", color: C.text, marginBottom: 18, lineHeight: 1.2 }}>
                {config.companyName}
              </h2>
              {config.aboutWhoWeAre && <p style={{ color: C.mid, lineHeight: 1.8, marginBottom: 14, fontSize: '0.97rem' }}>{config.aboutWhoWeAre}</p>}
              {config.aboutWhoWeAreBullets?.length > 0 && (
                <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
                  {config.aboutWhoWeAreBullets.map((b, i) => (
                    <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', color: C.mid, fontSize: '0.94rem', lineHeight: 1.6 }}>
                      <span style={{ color: acc, fontWeight: 700, flexShrink: 0, marginTop: 2 }}>✓</span>{b}
                    </li>
                  ))}
                </ul>
              )}
              {config.aboutMission && (
                <div style={{ borderLeft: `4px solid ${acc}`, background: `${acc}0c`, borderRadius: '0 12px 12px 0', padding: '14px 18px', marginTop: 16 }}>
                  <p style={{ color: C.mid, fontStyle: 'italic', lineHeight: 1.7, fontSize: '0.93rem' }}>{config.aboutMission}</p>
                </div>
              )}
              <button onClick={() => setModalOpen(true)} style={{ marginTop: 24, background: acc, color: '#fff', border: 'none', borderRadius: 100, padding: '12px 26px', fontWeight: 700, fontSize: '0.92rem', cursor: 'pointer' }}>
                Book a Consultation →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── MEET OUR DOCTORS ── */}
      {doctorsSection.enabled && doctors.length > 0 && (
        <section onMouseMove={chromaTrack} style={{ padding: 'clamp(56px,8vw,96px) 24px', background: C.bg, position: 'relative' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <div data-reveal style={{ ...R, textAlign: 'center', marginBottom: 40 }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <div style={{ width: 28, height: 2, background: acc, borderRadius: 2 }} />
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: acc, textTransform: 'uppercase', letterSpacing: '0.12em' }}>Our Team</span>
                <div style={{ width: 28, height: 2, background: acc, borderRadius: 2 }} />
              </div>
              <h2 style={{ fontSize: 'clamp(22px, 3vw, 36px)', fontWeight: 700, fontFamily: "'Kimbiso', 'DM Sans', sans-serif", color: C.text }}>
                {doctorsSection.title || 'Meet Our Doctors'}
              </h2>
            </div>
            <DoctorCarousel accent={acc}>
              {doctors.map((d, i) => (
                <div key={d.id} data-reveal style={{ ...R, transitionDelay: `${i * 0.05}s`, position: 'relative', background: C.card, borderRadius: 18, overflow: 'hidden', border: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', transition: 'transform 0.22s, box-shadow 0.22s' }}
                  >
                  {/* Photo */}
                  <div style={{ aspectRatio: '4 / 3', background: `linear-gradient(135deg, ${acc}22, ${acc}0a)`, position: 'relative', overflow: 'hidden' }}>
                    {d.photo ? (
                      <img src={d.photo} alt={d.name} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', transform: buildImgTransform({ scale: d.photo_scale, position: d.photo_position }), transformOrigin: 'center' }} />
                    ) : (
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '4rem', fontWeight: 800, color: acc, opacity: 0.4, fontFamily: "'Kimbiso', 'DM Sans', sans-serif" }}>
                        {d.name?.charAt(0)?.toUpperCase()}
                      </div>
                    )}
                  </div>
                  {/* Body */}
                  <div style={{ padding: '20px 22px 22px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: C.text, marginBottom: 4, fontFamily: "'Kimbiso', 'DM Sans', sans-serif" }}>{d.name}</h3>
                    {d.qualifications && (
                      <div style={{ fontSize: '0.78rem', color: C.muted, marginBottom: 8, letterSpacing: '0.02em' }}>{d.qualifications}</div>
                    )}
                    {d.specialization_name && (
                      <div style={{ display: 'inline-block', alignSelf: 'flex-start', fontSize: '0.7rem', fontWeight: 700, color: acc, background: `${acc}14`, padding: '3px 10px', borderRadius: 20, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                        {d.specialization_name}
                      </div>
                    )}
                    {d.experience_years > 0 && (
                      <div style={{ fontSize: '0.85rem', color: C.mid, marginBottom: 8, fontWeight: 500 }}>
                        <span style={{ color: acc, fontWeight: 700 }}>{d.experience_years}+</span> {d.experience_years === 1 ? 'year' : 'years'} of experience
                      </div>
                    )}
                    {d.bio && (
                      <p style={{ fontSize: '0.85rem', color: C.mid, lineHeight: 1.6, marginBottom: 6, display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{d.bio}</p>
                    )}
                    {d.bio && (
                      <button type="button" onClick={(e) => { e.stopPropagation(); setBioDoc((cur) => (cur === d.id ? null : d.id)); }}
                        style={{ alignSelf: 'flex-end', background: 'none', border: 'none', color: acc, fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer', padding: 0, marginBottom: 18 }}>
                        View full bio →
                      </button>
                    )}
                    <button
                      onClick={() => openBookWith(d.id, d.specialization)}
                      style={{ marginTop: 'auto', background: acc, color: '#fff', border: 'none', borderRadius: 100, padding: '10px 18px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', transition: 'opacity 0.15s' }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '0.92'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                    >
                      Book Appointment →
                    </button>
                  </div>

                  {/* Full-bio reveal — covers the card on hover/tap, reverts on mouse-leave */}
                  {d.bio && (
                    <div style={{ position: 'absolute', inset: 0, background: C.card, padding: 22, display: 'flex', flexDirection: 'column', gap: 8, opacity: bioDoc === d.id ? 1 : 0, pointerEvents: bioDoc === d.id ? 'auto' : 'none', transition: 'opacity 0.25s', overflowY: 'auto' }}>
                      <button type="button" onClick={() => setBioDoc(null)} style={{ alignSelf: 'flex-start', background: 'none', border: 'none', color: acc, fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer', padding: 0, marginBottom: 2 }}>← Back</button>
                      <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: C.text, fontFamily: "'Kimbiso', 'DM Sans', sans-serif", margin: 0 }}>{d.name}</h3>
                      {d.specialization_name && <div style={{ fontSize: '0.7rem', fontWeight: 700, color: acc, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{d.specialization_name}</div>}
                      <p style={{ fontSize: '0.84rem', color: C.mid, lineHeight: 1.65, margin: '4px 0 0', whiteSpace: 'pre-line' }}>{d.bio}</p>
                      <button onClick={() => openBookWith(d.id, d.specialization)}
                        style={{ marginTop: 'auto', background: acc, color: '#fff', border: 'none', borderRadius: 100, padding: '10px 18px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' }}>
                        Book Appointment →
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </DoctorCarousel>
          </div>
          <ChromaVeil />
        </section>
      )}

      {/* ── WHY CHOOSE US ── */}
      {whyChooseUs.length > 0 && (
        <section id="cl-whyus" style={{ padding: 'clamp(56px,8vw,96px) 24px', background: C.warm }}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <div data-reveal style={{ ...R, textAlign: 'center', marginBottom: 52 }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <div style={{ width: 28, height: 2, background: acc, borderRadius: 2 }} />
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: acc, textTransform: 'uppercase', letterSpacing: '0.12em' }}>Why Us</span>
                <div style={{ width: 28, height: 2, background: acc, borderRadius: 2 }} />
              </div>
              <h2 style={{ fontSize: 'clamp(22px, 3vw, 36px)', fontWeight: 700, fontFamily: "'Kimbiso', 'DM Sans', sans-serif", color: C.text, marginBottom: 8 }}>Why Patients Choose Us</h2>
              <p style={{ color: C.muted, maxWidth: 480, margin: '0 auto', lineHeight: 1.65 }}>Evidence-based care with a human touch</p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20 }}>
              {whyChooseUs.map((point, i) => (
                <div key={i} data-reveal
                  onMouseEnter={() => setHoveredWhy(i)}
                  onMouseLeave={() => setHoveredWhy(null)}
                  style={{ ...R, transitionDelay: `${i*0.07}s`, position: 'relative', overflow: 'hidden', background: hoveredWhy === i ? acc : C.card, borderRadius: 18, padding: '30px 26px', border: `1px solid ${hoveredWhy === i ? acc : C.border}`, cursor: 'default', transition: 'background 0.25s, border-color 0.25s, transform 0.2s, box-shadow 0.22s', transform: hoveredWhy === i ? 'translateY(-6px)' : 'none', boxShadow: hoveredWhy === i ? `0 18px 44px rgba(0,0,0,0.14)` : '0 2px 10px rgba(0,0,0,0.04)' }}>
                  {/* Accent strip */}
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${acc}, ${acc}80)`, borderRadius: '18px 18px 0 0', opacity: hoveredWhy === i ? 0 : 1, transition: 'opacity 0.25s' }} />
                  {/* Watermark */}
                  <div style={{ position: 'absolute', right: 14, top: 8, fontSize: '4rem', fontWeight: 900, color: hoveredWhy === i ? 'rgba(255,255,255,0.1)' : `${acc}10`, lineHeight: 1, userSelect: 'none', pointerEvents: 'none', transition: 'color 0.25s' }}>
                    {String(i+1).padStart(2,'0')}
                  </div>
                  {/* Icon circle */}
                  <div style={{ width: 48, height: 48, borderRadius: '50%', background: hoveredWhy === i ? 'rgba(255,255,255,0.18)' : `${acc}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16, transition: 'background 0.25s' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={hoveredWhy === i ? '#fff' : acc} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                  </div>
                  <p style={{ color: hoveredWhy === i ? 'rgba(255,255,255,0.92)' : C.mid, fontSize: '0.95rem', lineHeight: 1.68, margin: 0, fontWeight: hoveredWhy === i ? 500 : 400, transition: 'color 0.25s' }}>{point}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── FEATURES ── */}
      {features.length > 0 && (
        <section id="cl-services" style={{ padding: 'clamp(56px,8vw,96px) 24px', background: C.bg }}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <div data-reveal style={{ ...R, textAlign: 'center', marginBottom: 48 }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <div style={{ width: 28, height: 2, background: acc, borderRadius: 2 }} />
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: acc, textTransform: 'uppercase', letterSpacing: '0.12em' }}>Services</span>
                <div style={{ width: 28, height: 2, background: acc, borderRadius: 2 }} />
              </div>
              <h2 style={{ fontSize: 'clamp(22px, 3vw, 36px)', fontWeight: 700, fontFamily: "'Kimbiso', 'DM Sans', sans-serif", color: C.text }}>What We Offer</h2>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 22 }}>
              {features.map((f, i) => (
                <SpotlightCard key={i} glow={acc} radius={18} borderColor={C.border}
                  style={{ background: C.card }}>
                  <div data-reveal style={{ ...R, transitionDelay: `${i*0.06}s`, padding: '32px 26px' }}>
                    <div style={{ width: 56, height: 56, borderRadius: 16, background: `${acc}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18, fontSize: '1.5rem' }}>
                      {f.icon && typeof f.icon === 'string' && f.icon.length <= 4 ? f.icon : (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={acc} strokeWidth="1.8"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                      )}
                    </div>
                    <h3 style={{ fontWeight: 700, fontSize: '1.05rem', color: C.text, marginBottom: 8 }}>{f.title}</h3>
                    <p style={{ color: C.mid, fontSize: '0.9rem', lineHeight: 1.68 }}>{f.desc}</p>
                  </div>
                </SpotlightCard>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── CONDITIONS ── */}
      {conditions.length > 0 && (
        <section style={{ padding: 'clamp(56px,8vw,96px) 24px', background: C.warm }}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <div data-reveal style={{ ...R, textAlign: 'center', marginBottom: 48 }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <div style={{ width: 28, height: 2, background: acc, borderRadius: 2 }} />
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: acc, textTransform: 'uppercase', letterSpacing: '0.12em' }}>Specialties</span>
                <div style={{ width: 28, height: 2, background: acc, borderRadius: 2 }} />
              </div>
              <h2 style={{ fontSize: 'clamp(22px, 3vw, 36px)', fontWeight: 700, fontFamily: "'Kimbiso', 'DM Sans', sans-serif", color: C.text, marginBottom: 8 }}>Conditions We Treat</h2>
              {config.conditionsTagline && <p style={{ color: C.muted, maxWidth: 480, margin: '0 auto', lineHeight: 1.65 }}>{config.conditionsTagline}</p>}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
              {conditions.map((cond, i) => {
                const meta = enrichCondition(cond);
                return (
                  <SpotlightCard key={i} glow={acc} radius={16} borderColor={C.border}
                    style={{ background: C.card, borderTop: `3px solid ${acc}`, padding: '20px 18px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div data-reveal style={{ ...R, transitionDelay: `${i*0.05}s`, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <span style={{ alignSelf: 'flex-start', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', padding: '3px 9px', borderRadius: 20, background: `${acc}14`, color: acc }}>{meta.badge}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: '1.35rem' }}>{COND_EMOJIS[i % COND_EMOJIS.length]}</span>
                        <span style={{ fontSize: '0.94rem', fontWeight: 600, color: C.text, lineHeight: 1.35 }}>{cond}</span>
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
                        <span style={{ fontSize: '0.69rem', padding: '2px 8px', borderRadius: 20, background: 'rgba(0,0,0,0.05)', color: C.muted, fontWeight: 500 }}>👤 {meta.ages}</span>
                      </div>
                    </div>
                  </SpotlightCard>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ── OUR APPROACH ── */}
      {approach.length > 0 && (
        <section style={{ padding: 'clamp(56px,8vw,96px) 24px', background: C.bg }}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <div data-reveal style={{ ...R, textAlign: 'center', marginBottom: 52 }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <div style={{ width: 28, height: 2, background: acc, borderRadius: 2 }} />
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: acc, textTransform: 'uppercase', letterSpacing: '0.12em' }}>Methodology</span>
                <div style={{ width: 28, height: 2, background: acc, borderRadius: 2 }} />
              </div>
              <h2 style={{ fontSize: 'clamp(22px, 3vw, 36px)', fontWeight: 700, fontFamily: "'Kimbiso', 'DM Sans', sans-serif", color: C.text }}>Our Approach</h2>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 22 }}>
              {approach.map((step, i) => (
                <div key={i} data-reveal
                  onMouseEnter={() => setHoveredApproach(i)}
                  onMouseLeave={() => setHoveredApproach(null)}
                  style={{ ...R, transitionDelay: `${i*0.08}s`, background: hoveredApproach === i ? acc : C.card, borderRadius: 18, padding: '30px 24px', border: `1px solid ${C.border}`, textAlign: 'center', cursor: 'default', transition: 'background 0.24s, transform 0.2s, box-shadow 0.22s', transform: hoveredApproach === i ? 'translateY(-5px)' : 'none', boxShadow: hoveredApproach === i ? `0 16px 42px rgba(0,0,0,0.13)` : '0 2px 10px rgba(0,0,0,0.04)' }}>
                  <div style={{ width: 52, height: 52, borderRadius: '50%', background: hoveredApproach === i ? 'rgba(255,255,255,0.2)' : acc, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', boxShadow: hoveredApproach === i ? 'none' : `0 4px 14px ${acc}40` }}>
                    <span style={{ fontWeight: 800, fontSize: '1.05rem', color: '#fff' }}>{i+1}</span>
                  </div>
                  <p style={{ color: hoveredApproach === i ? 'rgba(255,255,255,0.9)' : C.mid, fontSize: '0.94rem', lineHeight: 1.68, margin: 0, transition: 'color 0.24s' }}>{step}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── TESTIMONIALS ── */}
      {testimonials.length > 0 && (
        <section id="cl-reviews" style={{ padding: 'clamp(56px,8vw,96px) 24px', background: C.warm }}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <div data-reveal style={{ ...R, textAlign: 'center', marginBottom: 48 }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <div style={{ width: 28, height: 2, background: acc, borderRadius: 2 }} />
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: acc, textTransform: 'uppercase', letterSpacing: '0.12em' }}>Reviews</span>
                <div style={{ width: 28, height: 2, background: acc, borderRadius: 2 }} />
              </div>
              <h2 style={{ fontSize: 'clamp(22px, 3vw, 36px)', fontWeight: 700, fontFamily: "'Kimbiso', 'DM Sans', sans-serif", color: C.text }}>What Our Patients Say</h2>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 20 }}>
              {testimonials.slice(0, 6).map((t, i) => {
                const name = [t.user__first_name, t.user__last_name].filter(Boolean).join(' ') || 'Patient';
                const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
                const rating = typeof t.rating === 'number' ? t.rating : 5;
                return (
                  <div key={i} data-reveal style={{ ...R, transitionDelay: `${i*0.06}s`, background: C.card, borderRadius: 18, padding: '26px', border: `1px solid ${C.border}`, transition: 'box-shadow 0.22s, border-color 0.22s' }}
                    onMouseEnter={e => { e.currentTarget.style.boxShadow = `0 10px 32px ${acc}14`; e.currentTarget.style.borderColor = `${acc}30`; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = C.border; }}>
                    <div style={{ fontSize: '2rem', color: acc, fontFamily: "'Kimbiso', 'DM Sans', sans-serif", lineHeight: 1, marginBottom: 10, opacity: 0.6 }}>"</div>
                    <p style={{ color: C.mid, fontSize: '0.91rem', lineHeight: 1.7, marginBottom: 16, fontStyle: 'italic' }}>{t.feedback}</p>
                    <div style={{ display: 'flex', gap: 1, marginBottom: 10 }}>
                      {[1,2,3,4,5].map(s => <span key={s} style={{ color: s <= rating ? '#f59e0b' : '#d1d5db', fontSize: '0.85rem' }}>★</span>)}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 38, height: 38, borderRadius: '50%', background: acc, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.85rem', flexShrink: 0 }}>{initials}</div>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '0.9rem', color: C.text }}>{name}</div>
                        <div style={{ fontSize: '0.72rem', color: C.muted }}>Verified Patient</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ── BRANCHES / LOCATIONS ── */}
      {locations.length > 0 && (
        <section style={{ padding: 'clamp(56px,8vw,96px) 24px', background: C.bg }}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <div data-reveal style={{ ...R, textAlign: 'center', marginBottom: 40 }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <div style={{ width: 28, height: 2, background: acc, borderRadius: 2 }} />
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: acc, textTransform: 'uppercase', letterSpacing: '0.12em' }}>Branches</span>
                <div style={{ width: 28, height: 2, background: acc, borderRadius: 2 }} />
              </div>
              <h2 style={{ fontSize: 'clamp(22px, 3vw, 36px)', fontWeight: 700, fontFamily: "'Kimbiso', 'DM Sans', sans-serif", color: C.text }}>Visit Us</h2>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 18 }}>
              {locations.map((loc, i) => {
                const isObj = typeof loc === 'object' && loc !== null;
                const name = isObj ? (loc.name || `Branch ${i + 1}`) : loc;
                const address = isObj ? loc.address : '';
                const phone = isObj ? loc.phone : '';
                return (
                  <div key={isObj && loc.id ? loc.id : i} data-reveal
                    style={{ ...R, transitionDelay: `${i*0.05}s`, background: C.card, borderRadius: 16, padding: '22px 22px', border: `1px solid ${C.border}` }}>
                    <a href={mapsUrl(loc)} target="_blank" rel="noopener noreferrer" title={address || name}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, textDecoration: 'none' }}>
                      <MapPinIcon size={18} color={acc} />
                      <h3 style={{ fontWeight: 700, fontSize: '1.02rem', color: C.text, margin: 0 }}>{name}</h3>
                    </a>
                    {address && (
                      <p style={{ color: C.mid, fontSize: '0.88rem', lineHeight: 1.6, marginBottom: phone ? 8 : 0 }}>{address}</p>
                    )}
                    {phone && (
                      <a href={`tel:${phone}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: acc, fontSize: '0.86rem', fontWeight: 600, textDecoration: 'none' }}>
                        <PhoneIcon size={13} /> {phone}
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ── GALLERY ── */}
      <CleanCarousel images={carousel.length > 0 ? carousel : (gallery.length > 0 ? gallery : CLEAN_GALLERY)} accent={acc} />

      {/* ── CONTACT / CTA ── */}
      <section id="cl-contact" style={{ background: acc, padding: 'clamp(56px,8vw,88px) 24px', textAlign: 'center' }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }} data-reveal>
          <h2 style={{ fontSize: 'clamp(24px, 3.5vw, 44px)', fontWeight: 700, color: '#fff', fontFamily: "'Kimbiso', 'DM Sans', sans-serif", marginBottom: 14, lineHeight: 1.2 }}>
            {config.closingCta || 'Start Your Healing Journey'}
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.8)', marginBottom: 32, fontSize: '1.02rem', lineHeight: 1.65, maxWidth: 520, margin: '0 auto 32px' }}>
            {config.footerTagline || 'Take the first step toward better health today.'}
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 14, flexWrap: 'wrap' }}>
            <button onClick={() => setModalOpen(true)} style={{ background: '#fff', color: acc, border: 'none', borderRadius: 100, padding: '14px 34px', fontSize: '1rem', fontWeight: 800, cursor: 'pointer', boxShadow: '0 6px 24px rgba(0,0,0,0.15)', transition: 'transform 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'none'}>
              Book Appointment
            </button>
            {whatsapp && (
              <a href={whatsapp} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#25d366', color: '#fff', borderRadius: 100, padding: '14px 30px', fontWeight: 700, fontSize: '1rem', textDecoration: 'none' }}>
                <WhatsAppIcon size={18} /> Chat on WhatsApp
              </a>
            )}
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer id="cl-contact" style={{ background: '#111', padding: '48px 24px 0', position: 'relative', overflow: 'hidden' }}>
        <SplashCursor colors={[acc, '#ffffff', '#22d3ee']} />
        <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div className="cl-footer-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1.5fr', gap: 36, paddingBottom: 40, borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: acc, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {config.logo ? <img src={config.logo} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', transform: config.logoTransform }} /> : <span style={{ color: '#fff', fontWeight: 800 }}>{config.companyName?.charAt(0)}</span>}
                </div>
                <span style={{ color: '#fff', fontWeight: 700 }}>{config.companyName}</span>
              </div>
              <p style={{ color: 'rgba(255,255,255,0.42)', fontSize: '0.83rem', lineHeight: 1.65 }}>{config.footerTagline || config.tagline}</p>
            </div>
            <div>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 14 }}>Links</div>
              {['About','Services','Reviews','Contact'].filter(l => !((l === 'Reviews' && testimonials.length === 0) || (l === 'Services' && features.length === 0))).map(l => (
                <a key={l} href={`#cl-${l.toLowerCase()}`} onClick={e => { e.preventDefault(); scrollToId(`cl-${l.toLowerCase()}`); }} style={{ display: 'block', fontSize: '0.85rem', color: 'rgba(255,255,255,0.55)', marginBottom: 7, textDecoration: 'none' }}
                  onMouseEnter={e => e.target.style.color = '#fff'}
                  onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.55)'}>{l}</a>
              ))}
            </div>
            <div>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 14 }}>Specialties</div>
              {conditions.slice(0, 5).map((c, i) => <div key={i} style={{ fontSize: '0.84rem', color: 'rgba(255,255,255,0.5)', marginBottom: 6 }}>{c.length > 30 ? c.slice(0,30)+'…' : c}</div>)}
            </div>
            <div>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 14 }}>Contact</div>
              {config.consultationHours && <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.84rem', marginBottom: 6 }}>🕐 {config.consultationHours}</div>}
              {config.contact_phone && <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.84rem', marginBottom: 6 }}>📞 {config.contact_phone}</div>}
              {config.contact_email && <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.84rem', marginBottom: 6 }}>✉️ {config.contact_email}</div>}
              {locations.length > 0 && (
                <div style={{ maxHeight: locations.length > 3 ? 240 : 'none', overflowY: locations.length > 3 ? 'auto' : 'visible', paddingRight: locations.length > 3 ? 6 : 0 }}>
                  {locations.map((loc, i) => {
                    const isObj = typeof loc === 'object' && loc !== null;
                    const name = isObj ? (loc.name || `Branch ${i + 1}`) : loc;
                    const addr = isObj ? loc.address : '';
                    const ph = isObj ? loc.phone : '';
                    return (
                      <div key={isObj && loc.id ? loc.id : i} style={{ marginBottom: 10 }}>
                        <a href={mapsUrl(loc)} target="_blank" rel="noopener noreferrer" title={addr || name} style={{ display: 'inline-block', color: 'rgba(255,255,255,0.78)', fontSize: '0.84rem', fontWeight: 600, marginBottom: 2, textDecoration: 'none' }}>📍 {name}</a>
                        {ph && <a href={`tel:${ph}`} style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 16, marginTop: 2, color: 'rgba(255,255,255,0.55)', fontSize: '0.78rem', textDecoration: 'none' }}><PhoneIcon size={12} /> {ph}</a>}
                      </div>
                    );
                  })}
                  <button type="button" onClick={() => setAddrOpen(true)} style={{ background: 'none', border: 'none', padding: 0, marginTop: 2, cursor: 'pointer', color: acc, fontSize: '0.8rem', fontWeight: 600, textDecoration: 'none' }}>
                    ⓘ View full address{locations.length > 1 ? 'es' : ''}
                  </button>
                </div>
              )}
              {whatsapp && (
                <a href={whatsapp} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10, background: '#25d366', color: '#fff', borderRadius: 50, padding: '7px 14px', fontSize: '0.8rem', fontWeight: 600, textDecoration: 'none' }}>
                  <WhatsAppIcon size={13} /> WhatsApp Us
                </a>
              )}
            </div>
          </div>
          <div style={{ padding: '18px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.28)' }}>© {new Date().getFullYear()} {config.companyName}. All rights reserved.</span>
            <span style={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.2)' }}>Powered by ZoidesQ</span>
          </div>
        </div>
      </footer>

      {/* ── Floating WhatsApp ── */}
      {whatsapp && (
        <a href={whatsapp} target="_blank" rel="noopener noreferrer"
          style={{ position: 'fixed', bottom: 24, left: 24, zIndex: 200, width: 52, height: 52, borderRadius: '50%', background: '#25d366', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(37,211,102,0.45)', textDecoration: 'none', transition: 'transform 0.2s' }}
          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}>
          <WhatsAppIcon size={26} />
        </a>
      )}

      <BookAppointmentModal
        id={companyData?.company_id}
        open={modalOpen}
        onClose={() => { setModalOpen(false); setPreselect({ doctorId: null, specId: null }); }}
        onSuccess={() => {}}
        accentColor={acc}
        preselectedDoctorId={preselect.doctorId}
        preselectedSpecId={preselect.specId}
      />
      <AddressesModal open={addrOpen} onClose={() => setAddrOpen(false)} locations={locations} accent={acc} />
    </div>
  );
}
